import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createEmployee } from '../services/firebase/employeeService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import PersonalDetailsForm from '../components/employee/PersonalDetailsForm';
import JobDetailsForm from '../components/employee/JobDetailsForm';
import BankDetailsForm from '../components/employee/BankDetailsForm';
import SalaryStructureForm from '../components/employee/SalaryStructureForm';
import '../assets/css/employee.css';

const AddEmployeePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    initialPassword: 'Welcome@123', // Default password
    personal: {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      maritalStatus: '',
      bloodGroup: '',
      photo: '',
      currentAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      permanentAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      emergencyContact: {
        name: '',
        relation: '',
        phone: ''
      }
    },
    job: {
      employeeCode: '',
      department: '',
      subDepartment: '',
      designation: '',
      reportingManager: '',
      employmentType: '',
      joiningDate: '',
      probationPeriod: 6,
      workLocation: '',
      role: 'employee'
    },
    bank: {
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      branchName: '',
      accountType: 'Savings',
      accountHolderName: ''
    },
    salary: {
      basicSalary: 0,
      hra: 0,
      da: 0,
      conveyance: 0,
      medicalAllowance: 0,
      otherAllowances: [],
      pf: 0,
      esi: 0,
      professionalTax: 0
    }
  });

  const [errors, setErrors] = useState({});

  // Check authentication on mount - wait for auth to load
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error('Please login to continue');
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const steps = [
    { number: 1, title: 'Personal Details', component: PersonalDetailsForm, key: 'personal' },
    { number: 2, title: 'Job Details', component: JobDetailsForm, key: 'job' },
    { number: 3, title: 'Bank Details', component: BankDetailsForm, key: 'bank' },
    { number: 4, title: 'Salary Structure', component: SalaryStructureForm, key: 'salary' }
  ];

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Setup password update handler
  useEffect(() => {
    window.updateInitialPassword = (password) => {
      setFormData(prev => ({
        ...prev,
        initialPassword: password
      }));
    };

    return () => {
      delete window.updateInitialPassword;
    };
  }, []);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.personal.firstName) newErrors.firstName = 'First name is required';
      if (!formData.personal.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.personal.email) newErrors.email = 'Email is required';
      if (!formData.personal.phone) newErrors.phone = 'Phone is required';
      if (!formData.personal.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (step === 2) {
      if (!formData.job.department) newErrors.department = 'Department is required';
      if (!formData.job.designation) newErrors.designation = 'Designation is required';
      if (!formData.job.joiningDate) newErrors.joiningDate = 'Joining date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      console.log('Submitting employee data:', formData);

      // Use Firebase service
      const response = await createEmployee(formData);

      if (response.success) {
        toast.success('Employee created successfully');
        navigate('/employees');
      }
    } catch (error) {
      console.error('Error creating employee:', error);

      const errorMessage = error.message || 'Failed to create employee';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;
  const currentStepKey = steps[currentStep - 1].key;
  const progress = (currentStep / steps.length) * 100;

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
        <Sidebar />
        <div className="flex-grow-1 employee-main-content">
          <Navbar />
          <Container fluid className="py-3 py-md-4 px-2 px-md-4">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading...</p>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 employee-main-content">
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4">
          <Row className="mb-3 mb-md-4">
            <Col>
              <h2 className="fw-bold text-dark mb-1 fs-4 fs-md-2">Add New Employee</h2>
              <p className="text-muted mb-0 small">Fill in the employee details</p>
            </Col>
          </Row>

          <Card className="shadow-sm border-0 rounded-4">
            <Card.Body className="p-3 p-md-4">
              {/* Progress Indicator */}
              <div className="mb-3 mb-md-4">
                <div className="d-flex justify-content-between mb-2">
                  {steps.map(step => (
                    <div
                      key={step.number}
                      className={`text-center ${currentStep >= step.number ? 'text-primary fw-bold' : 'text-muted'}`}
                      style={{ flex: 1 }}
                    >
                      <div
                        className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-1 ${currentStep >= step.number ? 'bg-primary text-white shadow-sm' : 'bg-light'
                          }`}
                        style={{ width: '34px', height: '34px', fontSize: '0.85rem' }}
                      >
                        {step.number}
                      </div>
                      <div className="text-truncate px-1" style={{ fontSize: '0.72rem' }}>{step.title}</div>
                    </div>
                  ))}
                </div>
                <ProgressBar now={progress} variant="primary" style={{ height: '6px' }} />
              </div>

              {/* Form Content */}
              <CurrentStepComponent
                data={formData[currentStepKey]}
                onChange={handleInputChange}
                errors={errors}
                formData={formData}
              />

              {/* Navigation Buttons */}
              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                >
                  Previous
                </Button>

                <div>
                  <Button
                    variant="outline-secondary"
                    className="me-2"
                    onClick={() => navigate('/employees')}
                  >
                    Cancel
                  </Button>

                  {currentStep < 4 ? (
                    <Button variant="primary" onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Employee'}
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default AddEmployeePage;
